import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import DynamicForm from '../../components/DynamicForm';
import { campaignFormFields } from './CampaignFieldsConfig';
import { getAllOtaArtifacts, createCampaign, getDevicesByModel } from '../../commonApi';
import { extractErrorMessage } from '../../Utils/ExtractMessages';
import { toast } from 'react-toastify';

const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

const buildDeviceOptionsLoader = (deviceModelId) => debounce(async (inputValue, callback) => {
    try {
        const response = await getDevicesByModel(deviceModelId, {
            pageNo: 0,
            pageSize: 500,
            searchValue: inputValue,
        });
        const devices = (response?.data?.content || []).filter(
            device => device && typeof device === 'object' && device.thingName
        );
        callback(devices.map(device => ({
            value: device.thingName,
            label: device.userAssignedDeviceName
                ? `${device.userAssignedDeviceName} (${device.thingName})`
                : device.thingName,
            userAssignedDeviceName: device.userAssignedDeviceName,
            thingName: device.thingName,
        })));
    } catch (error) {
        console.error("Error fetching devices for model:", error);
        toast.error(extractErrorMessage(error));
        callback([]);
    }
}, 300);

const AddCampaign = ({ show, onClose, onSuccess }) => {
    const [fields, setFields] = useState(campaignFormFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- new object each time `show` flips true so DynamicForm's reset effect fires
    const defaultValues = useMemo(() => ({ targetMode: 'ALL_DEVICES' }), [show]);

    useEffect(() => {
        if (show) {
            fetchArtifacts();
        }
    }, [show]);

    const handleArtifactChange = (val, setValue) => {
        setValue('targetDeviceIds', []);

        const deviceModelId = val?.deviceModelId;

        setFields(prevFields => prevFields.map(field =>
            field.name === 'targetDeviceIds'
                ? {
                    ...field,
                    loadOptions: deviceModelId ? buildDeviceOptionsLoader(deviceModelId) : null,
                    cacheKey: deviceModelId || null,
                }
                : field
        ));
    };

    const fetchArtifacts = async () => {
        try {
            const response = await getAllOtaArtifacts({ pageNo: 0, pageSize: 100 }); // Adjust params as needed
            const artifacts = response.data.data.content || [];

            const artifactOptions = artifacts.map(artifact => ({
                value: artifact.id,
                label: `${artifact.artifactName}`,
                artifactName: artifact.artifactName,
                deviceModelId: artifact.deviceModelId,
            }));

            setFields(prevFields => prevFields.map(field =>
                field.name === 'otaArtifactId'
                    ? { ...field, options: artifactOptions, onChange: handleArtifactChange }
                    : field
            ));

        } catch (error) {
            console.error("Error fetching artifacts:", error);
            toast.error("Failed to load OTA Artifacts");
        }
    };

    const handleSubmit = async (formData) => {
        try {

            const payload = {
                name: formData.jobId,
                description: formData.description,
                artifactId: formData.otaArtifactId?.value,
                expiryDays: formData.expiryDays?.value,
            };

            if (formData.targetMode === 'SPECIFIC_DEVICES') {
                payload.targetDeviceIds = (formData.targetDeviceIds || []).map(device => device.value);
            }

            await createCampaign(payload);
            toast.success("Campaign created successfully!");
            onClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error("Error creating campaign:", error);
            toast.error(extractErrorMessage(error));
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ color: 'rgb(67, 64, 210)' }}>Create New Campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DynamicForm
                    fields={fields}
                    defaultValues={defaultValues}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                    onError={(errors) => console.log("Form Errors:", errors)}
                />
            </Modal.Body>
        </Modal>
    );
};

export default AddCampaign;
